"use client";
import { faHourglassStart } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslations } from "next-intl";
import React, { FC, ReactNode, useMemo } from "react";

import Button from "@/components/ui/button";
import { useAuth } from "@/contexts/auth_context";
import { useModal } from "@/contexts/modal_context";
import cn from "@/utils/core/cn";

type Props = {
  predictLabel?: string;
  onSubmit: () => void;
  isDirty: boolean;
  hasUserForecast: boolean;
  isUserForecastActive?: boolean;
  isPending: boolean;
  isDisabled?: boolean;
  predictionExpirationChip?: ReactNode;
  onPredictionExpirationClick?: () => void;
};

const PredictButton: FC<Props> = ({
  predictLabel,
  hasUserForecast,
  isUserForecastActive,
  isPending,
  isDirty,
  onSubmit,
  isDisabled,
  predictionExpirationChip,
  onPredictionExpirationClick,
}) => {
  const { user } = useAuth();
  const { setCurrentModal } = useModal();
  const t = useTranslations();

  const disabled = useMemo(() => {
    if (!user) {
      return false;
    }

    if (user.is_bot) {
      return true;
    }

    if (isDisabled) {
      return true;
    }

    if (isPending) {
      return true;
    }

    if (hasUserForecast) {
      // If isUserForecastActive is provided, use it to determine if forecast is active
      // If not provided, fall back to old behavior (assume active if hasUserForecast is true)
      if (isUserForecastActive !== undefined) {
        // Disable only if forecast is not active AND there are no dirty changes
        return !isUserForecastActive && !isDirty;
      }
      // Fallback: if isUserForecastActive is not provided, use old behavior
      return false;
    }

    return !isDirty;
  }, [
    hasUserForecast,
    isDirty,
    isDisabled,
    isPending,
    user,
    isUserForecastActive,
  ]);
  const buttonLabel = useMemo(() => {
    if (!user) {
      return t("signUpToPredict");
    }

    if (hasUserForecast && !isDirty && isUserForecastActive) {
      return t("reaffirm");
    }

    return predictLabel ?? t("saveChange");
  }, [hasUserForecast, isDirty, predictLabel, t, user, isUserForecastActive]);

  const handleClick = () => {
    if (!user) {
      setCurrentModal({ type: "signin" });
      return;
    }

    onSubmit();
  };

  const showPredictionExpirationChip = !!predictionExpirationChip && !!user;

  return (
    <div className="flex">
      <Button
        variant="primary"
        type="submit"
        disabled={disabled}
        onClick={handleClick}
        className={cn("", {
          "rounded-r-none": showPredictionExpirationChip,
        })}
      >
        {buttonLabel}
      </Button>

      {showPredictionExpirationChip && (
        <Button
          variant="secondary"
          onClick={() => onPredictionExpirationClick?.()}
          disabled={disabled}
          className="gap-1 rounded-l-none px-1.5"
        >
          <FontAwesomeIcon icon={faHourglassStart} />
          {predictionExpirationChip}
        </Button>
      )}
    </div>
  );
};

export default PredictButton;
